import { useCallback, useEffect, useState } from "react"

import { getContactConsent } from "../api/consent.ts"
import { onConsentChange } from "../consent/consent-events.ts"
import type { ContactRef } from "../types/compose.types.ts"
import type { ContactConsent } from "../types/consent.types.ts"
import { contactKey } from "./record-contacts.ts"

export type ConsentLoad =
  | { state: "loading" }
  | { state: "ready"; consent: ContactConsent }
  | { state: "forbidden" }
  | { state: "failed" }

type Listener = () => void

const entries = new Map<string, ConsentLoad>()
const inFlight = new Map<string, Promise<void>>()
const listeners = new Map<string, Set<Listener>>()

const notify = (key: string) =>
  listeners.get(key)?.forEach((listener) => listener())

const fetchConsent = (ref: ContactRef) => {
  const key = contactKey(ref)
  const running = inFlight.get(key)
  if (running) return running
  if (!entries.has(key)) entries.set(key, { state: "loading" })
  const request = getContactConsent(ref)
    .then((response) => {
      entries.set(
        key,
        response.ok && response.data
          ? { state: "ready", consent: response.data }
          : response.status === 403 || response.status === 404
            ? { state: "forbidden" }
            : { state: "failed" }
      )
    })
    .catch(() => {
      entries.set(key, { state: "failed" })
    })
    .finally(() => {
      inFlight.delete(key)
      notify(key)
    })
  inFlight.set(key, request)
  return request
}

/** Puts a saved answer in place, so every reader of this contact shows it. */
export const storeConsent = (ref: ContactRef, consent: ContactConsent) => {
  const key = contactKey(ref)
  entries.set(key, { state: "ready", consent })
  notify(key)
}

// A recorded change can speak for more than the contact it was made on (a
// lead and its customer share one person), so every contact on screen is read again.
onConsentChange(() => {
  Array.from(entries.keys()).forEach((key) => {
    if (!listeners.get(key)?.size) {
      entries.delete(key)
      return
    }
    const [type, id] = key.split(":")
    void fetchConsent({ type, id } as ContactRef)
  })
})

/** Forgets every cached answer, for tests. */
export const resetConsentStore = () => {
  entries.clear()
  inFlight.clear()
}

/**
 * One contact's consent and history, read once and shared by the header
 * buttons, the contact strip and the preferences drawer.
 */
export const useContactConsent = (ref: ContactRef | null) => {
  const key = ref ? contactKey(ref) : null
  const [, setTick] = useState(0)

  useEffect(() => {
    if (!key) return
    const listener = () => setTick((tick) => tick + 1)
    const set = listeners.get(key) ?? new Set<Listener>()
    set.add(listener)
    listeners.set(key, set)
    const [type, id] = key.split(":")
    if (!entries.has(key) || entries.get(key)?.state === "failed")
      void fetchConsent({ type, id } as ContactRef)
    return () => {
      set.delete(listener)
    }
  }, [key])

  const reload = useCallback(() => {
    if (!key) return
    const [type, id] = key.split(":")
    entries.set(key, { state: "loading" })
    notify(key)
    void fetchConsent({ type, id } as ContactRef)
  }, [key])

  const load: ConsentLoad = key
    ? (entries.get(key) ?? { state: "loading" })
    : { state: "loading" }
  return { load, reload }
}
